<template lang="pug">
  .datepicker(
  :class="{open: isOpen, above: preferredOpenDirection === 'above', 'datepicker-disabled': disabled}"
  @blur="deactivate"
  @focus="activate"
  @keyup.esc="deactivate"
  )

    .caret(@mousedown.prevent.stop="toggle")

    .input-wrapper
      input(
      ref="input"
      type="text"
      v-model="displayText"
      :disabled="disabled"
      :placeholder="placeholder"
      :tabindex="tabindex"
      @blur.prevent="onInputBlur"
      @focus.prevent="activate"
      @keydown.down.prevent="pointerDown"
      @keydown.enter.prevent.stop.self="deactivate"
      @keydown.left.prevent="pointerLeft"
      @keydown.right.prevent="pointerRight"
      @keydown.up.prevent="pointerUp"
      @keyup.esc="deactivate"
      )

    transition(name="datepicker")
      .content-wrapper(
      v-show="isOpen"
      @focus="activate"
      @mousedown.prevent=""
      )

        datepicker-range(
        :min="min"
        :max="max"
        ref="picker"
        v-model="selectedDate"
        @input="onDateInput"
        )
</template>

<script>
import DatepickerRange from './datepicker-range.vue';

export default {
    components: {
        DatepickerRange,
    },

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },

        format: {
            type: String,
            default: 'YYYY-MM-DD',
        },

        // eslint-disable-next-line vue/require-prop-types
        min: {
            default: null,
            validator(value) {
                return (
                    value === null ||
                    value instanceof Date ||
                    typeof value === 'string' ||
                    value instanceof String ||
                    value instanceof moment
                );
            },
        },

        // eslint-disable-next-line vue/require-prop-types
        max: {
            default: null,
            validator(value) {
                return (
                    value === null ||
                    value instanceof Date ||
                    typeof value === 'string' ||
                    value instanceof String ||
                    value instanceof moment
                );
            },
        },

        maxHeight: {
            type: Number,
            default: 250,
        },

        maxWidth: {
            type: Number,
            default: 250,
        },

        placeholder: {
            type: String,
            default: '',
            required: false,
        },

        tabindex: {
            type: Number,
            default: 0,
        },

        // eslint-disable-next-line vue/require-prop-types,vue/require-default-prop
        value: {
            required: false,
        },
    },

    data() {
        return {
            displayText: '',
            isOpen: false,
            preferredOpenDirection: 'below',
            selectedDate: null,
        };
    },

    watch: {
        value(newValue) {
            this.updateSelectedDate(newValue, false);
        },
    },

    created() {
        window.addEventListener('scroll', this.onWindowScroll);
    },

    mounted() {
        this.updateSelectedDate(this.value);
    },

    destroyed() {
        window.removeEventListener('scroll', this.onWindowScroll);
    },

    methods: {
        activate(e) {
            if (this.isOpen) return;
            this.adjustPosition();

            this.isOpen = true;
            this.$refs.picker.resetView();
            this.$refs.input.focus();
        },

        adjustPosition() {
            if (typeof window === 'undefined') return;

            const spaceAbove = this.$el.getBoundingClientRect().top;
            const spaceBelow =
                window.innerHeight - this.$el.getBoundingClientRect().bottom;
            const hasEnoughSpaceBelow = spaceBelow > this.maxHeight;

            if (hasEnoughSpaceBelow || spaceBelow > spaceAbove) {
                this.preferredOpenDirection = 'below';
            } else {
                this.preferredOpenDirection = 'above';
            }
        },

        deactivate(e) {
            if (!this.isOpen) return;

            this.isOpen = false;
            this.$refs.input.blur();
        },

        onDateSelected(date) {
            let m = moment(date);

            if (m.isValid()) {
                this.displayText = m.format(this.format);
            }
        },

        onDateInput(date, source) {
            this.onDateSelected(date);
            if (source === 'mouse') this.deactivate();
        },

        onInputBlur(e) {
            this.deactivate(e);
            this.updateSelectedDate(this.displayText);
        },

        onWindowScroll(e) {
            this.adjustPosition();
        },

        pointerDown() {
            this.$refs.picker.selectWeekAfter();
        },

        pointerLeft() {
            this.$refs.picker.selectDayBefore();
        },

        pointerRight() {
            this.$refs.picker.selectDayAfter();
        },

        pointerUp() {
            this.$refs.picker.selectWeekBefore();
        },

        toggle() {
            if (this.disabled) return;
            this.isOpen ? this.deactivate() : this.activate();
        },

        updateSelectedDate(value, broadcast = true) {
            if (_.isEmpty(value)) {
                this.selectedDate = null;
                this.displayText = '';

                if (broadcast) {
                    this.$emit('input', this.selectedDate);
                }

                return;
            }

            let m = moment(value, this.format);

            if (m.isValid()) {
                this.selectedDate = m;

                if (broadcast) {
                    this.$emit('input', this.selectedDate);
                }

                this.displayText = m.format(this.format);
            } else {
                this.displayText = '';
            }
        },
    },
};
</script>

<style lang="scss">
@import '../../../sass/mixins';
@import '../../../sass/variables';

%body {
    height: 28px;
    line-height: 28px;
    text-align: center;
    @include user-select(none);
}

%body-hover {
    @extend %body;
    cursor: pointer;

    &:hover {
        background-color: #eeeeee;
    }
}

.has-danger {
    .datepicker {
        .input-wrapper {
            border-color: $error-color;
        }
    }
}

.datepicker-enter-active,
.datepicker-leave-active {
    transition: all 0.15s ease;
}

.datepicker-enter,
.datepicker-leave-active {
    opacity: 0;
}

fieldset:disabled,
.datepicker.datepicker-disabled {
    .input-wrapper {
        background: #eceeef;

        input {
            background: #eceeef;
        }
    }
}

.datepicker {
    box-sizing: content-box;
    color: #35495e;
    display: block;
    min-height: 40px;
    position: relative;
    text-align: left;
    touch-action: manipulation;
    width: 100%;

    * {
        box-sizing: border-box;
    }

    &.above {
        &.open {
            .input-wrapper {
                border-bottom-left-radius: 5px;
                border-bottom-right-radius: 5px;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
            }
        }

        .content-wrapper {
            border-bottom: none;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            border-top: 1px solid $input-border-focus;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            bottom: 100%;
        }
    }

    &.open {
        z-index: 50;

        .caret {
            transform: rotateZ(180deg);
        }

        .input-wrapper {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            border-color: $input-border-focus;
        }
    }

    .caret {
        box-sizing: border-box;
        color: #a6a6a6;
        cursor: pointer;
        display: block;
        height: 38px;
        line-height: 16px;
        margin: 0;
        padding: 4px 8px;
        position: absolute;
        right: 1px;
        text-align: center;
        text-decoration: none;
        top: 1px;
        transition: transform 0.2s ease;
        width: 40px;

        &:before {
            border-color: #999999 transparent transparent transparent;
            border-style: solid;
            border-width: 5px 5px 0 5px;
            color: #999;
            content: '';
            margin-top: 4px;
            position: relative;
            right: 0;
            top: 65%;
        }
    }

    .content-wrapper {
        background: $white;
        border: 1px solid $input-border-focus;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
        border-top: none;
        display: block;
        max-width: 250px;
        overflow: auto;
        overflow-scrolling: touch;
        padding: 10px;
        position: absolute;
        right: 0;
        width: 100%;
        z-index: 50;
    }

    .input-wrapper {
        background: $white;
        border: 1px solid $input-border-color;
        border-radius: 5px;
        display: block;
        min-height: 40px;
        padding: 8px 40px 0 8px;

        input {
            position: relative;
            display: inline-block;
            min-height: 20px;
            line-height: 20px;
            border: none;
            border-radius: 5px;
            background: #fff;
            padding: 0 0 0 5px;
            width: calc(100%);
            transition: border 0.1s ease;
            box-sizing: border-box;
            margin-bottom: 8px;
            vertical-align: top;
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/datepicker/datepicker.vue